import { Link } from "react-router-dom";
import { Banknote, ShieldCheck, Wrench, Info } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTABand from "../components/CTABand";
import PageMeta from "../components/PageMeta";

const SERVICES = [
  {
    id: "financing", icon: Banknote, eyebrow: "Healthcare Financing Support",
    title: "Making Healthcare Projects Financially More Accessible.",
    body: "Healthcare infrastructure requires significant capital investment. AccuSurge can support clients in exploring financing options through appropriate banking and financial channels for eligible healthcare projects.",
    note: "AccuSurge provides support and assistance in exploring financing options. AccuSurge is not a lender and does not provide direct lending or guarantees of loan approval.",
    cta: { label: "Discuss Your Project", to: "/contact" },
    points: ["Guidance on suitable financing channels", "Documentation support for equipment/project proposals", "Coordination with banking and financial partners", "Project costing support for funding discussions"],
  },
  {
    id: "licensing", icon: ShieldCheck, eyebrow: "Licensing & Accreditation Support",
    title: "Navigate Compliance With Confidence.",
    body: "Setting up a healthcare facility involves multiple regulatory, licensing and compliance requirements. AccuSurge supports clients in navigating relevant requirements and coordinating the necessary processes with appropriate professionals and authorities.",
    note: "AccuSurge coordinates and supports compliance processes. Licenses and accreditations are granted solely by the relevant authorities and accreditation bodies.",
    cta: { label: "Discuss Compliance Requirements", to: "/contact" },
    points: ["Healthcare facility licensing", "Regulatory coordination", "Accreditation preparation", "Documentation support", "Facility readiness", "Compliance coordination"],
  },
  {
    id: "after-sales", icon: Wrench, eyebrow: "After-Sales Service",
    title: "Support Doesn't End After Installation.",
    body: "Our relationship with clients continues beyond delivery and installation. AccuSurge provides after-sales support to help healthcare facilities maintain operational continuity.",
    note: null,
    cta: { label: "Request Service Support", to: "/contact?intent=service" },
    points: ["Preventive maintenance", "Breakdown support", "Repair", "Equipment servicing", "Technical assistance", "Spare parts coordination", "AMC support", "Replacement support"],
  },
];

const Services = () => (
  <main data-testid="services-page">
    <PageMeta title="Services — Financing, Licensing & After-Sales | AccuSurge" description="Healthcare financing support, licensing & accreditation support and after-sales service including preventive maintenance, breakdown support and AMC coordination." />
    <section className="bg-brand-950 pb-20 pt-40 md:pb-28 grain relative overflow-hidden">
      <div className="container-x relative">
        <Reveal>
          <span className="eyebrow text-accent">Services</span>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white">
            Beyond Supply. Into Partnership.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70">
            Financing assistance, licensing and accreditation coordination and long-term after-sales service — the
            parts of a healthcare project that continue long after the invoice.
          </p>
        </Reveal>
      </div>
    </section>

    {SERVICES.map((s, i) => (
      <section key={s.id} id={s.id} className={`scroll-mt-32 py-20 md:py-28 ${i % 2 ? "bg-slate-100" : "bg-white"}`} data-testid={`service-${s.id}`}>
        <div className="container-x grid gap-14 lg:grid-cols-2 lg:items-start">
          <Reveal>
            <span className="eyebrow">{s.eyebrow}</span>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-ink text-balance">{s.title}</h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-slate-600">{s.body}</p>
            <Link to={s.cta.to} className="btn-accent mt-9" data-testid={`service-cta-${s.id}`}>{s.cta.label}</Link>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid={`service-points-${s.id}`}>
              <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-slate-400">What this includes</h3>
              <Stagger className="mt-6 grid gap-3 sm:grid-cols-2">
                {s.points.map((p) => (
                  <StaggerItem key={p}>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">{p}</div>
                  </StaggerItem>
                ))}
              </Stagger>
              {s.note && (
                <p className="mt-6 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-accent-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800" data-testid={`service-disclaimer-${s.id}`}>
                  <Info className="mt-0.5 h-4 w-4 shrink-0" /> {s.note}
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    ))}

    <CTABand />
  </main>
);

export default Services;
